import { useCallback, useContext, useEffect, useState } from "react";
import InnerLayout from "../../../components/layouts/inner-layout";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { api } from "../../../redux/apis/api";
import successToast from "../../../components/helper-components/success-toast";
import errorToast from "../../../components/helper-components/error-toast";

import { storeDetailsAction } from "../../../redux/actions/invoices-action";
import LoadingSpinner from "../../../components/helper-components/loading-spinner";
import moment from "moment";
import { DataContext } from "../../../components/layouts/main-layout";
import {
  purchaseTableAction,
  purchaseViewAction,
  purchaseVoidAction,
} from "../../../redux/actions/purchase-action";

import DynamicTransactionTable from "../../../components/tables/dynamic-transaction-table";
import SalesPurchaseFilter from "../../../components/sales-section/sales-purchase-filter";
import BadgesComponent from "../../../components/tables/badges-component";
import TransSummaryComp from "../../../components/helper-components/trans-summary-comp";
import {
  ChartColumnIncreasing,
  FileText,
  MoveDownLeft,
  TimerIcon,
} from "lucide-react";
import { formatIndianNumber } from "../../../components/helper-components/helper-function";
import { isAdmin } from "../../../redux/config";
import {
  getRowSyncState,
  setSyncFrozenNow,
} from "../../../components/helper-components/sync-freeze";
import useSyncFreezeTick from "../../../components/helper-components/use-sync-freeze-tick";

const PurchaseTable = () => {
  useSyncFreezeTick();
  const [showFilter, setShowFilter] = useState(false);
  const [heading, setHeading] = useState([]);
  const [billSummary, setBillSummary] = useState({});

  const { permissionFn } = useContext(DataContext);
  const dispatch = useDispatch();
  const [reload, setReload] = useState(false);
  const [orders, setOrders] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [action, setAction] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [storeDetails, setStoreDetails] = useState({});
  const [syncStates, setSyncStates] = useState({});

  useEffect(() => {
    dispatch(storeDetailsAction(onSuccessStoreDetails, onErrorStoreDetails));
    const data = localStorage.getItem("ModPermission");
    if (data) {
      const parsed = JSON.parse(data);
      const potData = parsed.find((obj) => obj?.purchase_order_list);
      if (potData?.purchase_order_list) {
        setHeading(potData.purchase_order_list);
      }
    }
  }, []);
  const onSuccessStoreDetails = (data) => {
    setStoreDetails(data?.data);
  };
  const onErrorStoreDetails = () => {};

  const handleHardSync = async (potId, e) => {
    e.stopPropagation();
    const item = orders.find((o) => o?.pot_id === potId);
    const syncState = getRowSyncState({
      moduleKey: "purchase_order",
      id: potId,
      dataSynced: item?.data_synced,
      lastSynced: item?.last_synced,
    });
    if (syncState.disabled) return;
    setSyncStates((prev) => ({ ...prev, [potId]: "syncing" }));
    try {
      const res = await api.get(`/purchaseorder/hard_sync?pot_id=${potId}`);
      if (res?.data?.status === false) {
        errorToast(res?.data?.message ?? "Sync failed");
      } else {
        setSyncFrozenNow(`purchase_order_${potId}`);
        successToast(res?.data?.message ?? "Sync successful");
      }
    } catch (err) {
      errorToast(err?.response?.data?.message ?? "Sync failed");
    }
    setSyncStates((prev) => ({ ...prev, [potId]: "synced" }));
  };

  const [filterData, setFilterData] = useState({
    sort_by: "",
    sort_order: "",
    // selectColumn: "pot_invoice_number",
    selectColumn: "",
    from: "",
    to: "",
    inputValue: "",
    orderStatus: "",
  });
  useEffect(() => {
    dispatch(
      purchaseTableAction(
        {
          pageNumber: currentPage,
          pageSize: pageSize,
          filterData,
        },
        onSuccess,
        onError
      )
    );
  }, [currentPage, pageSize, filterData, reload]);
  const onSuccess = (data) => {
    setError(false);
    setLoading(false);
    setOrders(data?.data?.orderList ?? []);
    setDataCount(data?.data?.totalCount);
    setBillSummary(data?.data?.orderStats);
  };
  const onError = (data) => {
    setError(true);
    setLoading(false);
  };
  const navigate = useNavigate();
  const paginationProps = {
    pageSize: pageSize,
    setPageSize: setPageSize,
    dataCount: dataCount,
    setCurrentPage: setCurrentPage,
    currentPage: currentPage,
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        action &&
        currentIndex != null &&
        !event.target.closest(".relative")
      ) {
        setAction(false);
        setCurrentIndex(null);
      }
    };
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [action, currentIndex]);

  const tableBodyData = orders?.map((item) => {
    const syncState = getRowSyncState({
      moduleKey: "purchase_order",
      id: item?.pot_id,
      dataSynced: item?.data_synced,
      lastSynced: item?.last_synced,
    });
    return {
      id: item?.pot_id,
      pot_invoice_number:
        (
          <span className="fw_500 cursor-pointer">
            {item?.pot_invoice_number ?? "--"}
          </span>
        ) ?? "--",
      cmr_name: item?.cmr_name ?? "--",
      created_date: item?.created_date
        ? moment(item?.created_date).format("DD MMM YYYY")
        : "--",
      update_date: item?.update_date
        ? moment(item?.update_date).format("DD MMM YYYY")
        : "--",
      created_user_name: item?.created_user_name ?? "--",
      updated_user_name: item?.updated_user_name ?? "--",
      order_status: <BadgesComponent status={item?.pot_order_status} />,
      pot_total_amount: "₹ " + item?.pot_total_amount,
      is_draft_order: item?.is_draft_order,
      itemData: item?.itemData?.length > 0 ? item?.itemData : [],
      cmr_id: item?.cmr_id ?? "--",
      pot_order_status: item?.pot_order_status,
      is_voided: item?.is_voided ?? false,
      store_code: item?.store_code ?? "--",
      store_name: item?.store_name ?? "--",
      store_type: item?.store_type ?? "--",
      firm_name: item?.firm_name ?? "--",
      cmr_code: item?.cmr_code ?? "--",
      pot_reference_inv_number:item?.pot_reference_inv_number?? "--",
      pot_reference_inv_date: item?.pot_reference_inv_date? new Date(item.pot_reference_inv_date).toISOString().split("T")[0]: "--",
      sync_action: (
        <button
          disabled={syncStates[item?.pot_id] === "syncing" || syncState.disabled}
          onClick={(e) => handleHardSync(item?.pot_id, e)}
          title={syncState.synced ? `Last synced: ${syncState.label}` : ""}
          className="border-radius hover:bg-[var(--primary-button-hover-color)] flex justify-center h-[32px] px-[12px] items-center whitespace-nowrap transition duration-300 ease-in-out text-[length:--primary-button-font-size] font-[var(--primary-button-font-weight)] bg-[var(--primary-button-bg-color)] text-[var(--primary-button-fg-color)] disabled:opacity-50 disabled:cursor-not-allowed min-w-[64px]"
        >
          {syncStates[item?.pot_id] === "syncing"
            ? "Initiating..."
            : syncState.label}
        </button>
      ),
    };
  });

  const filterby = showFilter ? (
    <SalesPurchaseFilter
      filterData={filterData}
      setFilterData={setFilterData}
      setCurrentPage={setCurrentPage}
    />
  ) : null;
  const handleFilterClick = useCallback(() => {
    setShowFilter((prevShowFilter) => !prevShowFilter);
  }, []);

  const totalGRNs = billSummary?.totalGRNs ?? 0;
  const averageGRNValue = billSummary?.averageGRNValue ?? 0;
  const pendingGRNs = billSummary?.pendingGRNs ?? 0;
  const totalQuantityReceived = billSummary?.totalQuantityReceived ?? 0;

  const TransactionSummary = [
    {
      conClass:
        "bg-[var(--trans-stats-con-green-bg)] rounded-[12px] h-[5.3rem] w-[25%] p-3 flex justify-between",
      label: "Total Qty Received",
      value: totalQuantityReceived?.count ?? 0,
      desc: `₹${formatIndianNumber(
        totalQuantityReceived?.value || 0
      )} received`,
      descClass:
        "text-[length:var(--trans-stats-con-desc-fs)] font-[var(--trans-stats-con-desc-fw)] text-[var(--trans-stats-con-green-fg)]",
      icon: <MoveDownLeft size={16} color="var(--trans-stats-con-green-fg)" />,
      labelClass:
        "text-[length:var(--trans-stats-con-label-fs)] font-[var(--trans-stats-con-label-fw)] text-[var(--trans-stats-con-label-fg)]",
      valueClass:
        "text-[length:var(--trans-stats-con-value-fs)] font-[var(--trans-stats-con-value-fw)] text-[var(--trans-stats-con-value-fg)] mt-2",
    },
    {
      conClass:
        "bg-[var(--trans-stats-con-orange-bg)] rounded-[12px] h-[5.3rem] w-[25%] p-3 flex justify-between",
      label: "Avg. GRN Value",
      value: `₹ ${formatIndianNumber(averageGRNValue?.value || 0)}`,
      desc: `${averageGRNValue?.changePercentage < 0 ? "" : "+"}${
        averageGRNValue?.changePercentage || 0
      }% vs last week`,
      descClass:
        "text-[length:var(--trans-stats-con-desc-fs)] font-[var(--trans-stats-con-desc-fw)] text-[var(--trans-stats-con-orange-fg)]",
      icon: (
        <ChartColumnIncreasing
          size={16}
          color="var(--trans-stats-con-orange-fg)"
        />
      ),
      labelClass:
        "text-[length:var(--trans-stats-con-label-fs)] font-[var(--trans-stats-con-label-fw)] text-[var(--trans-stats-con-label-fg)]",
      valueClass:
        "text-[length:var(--trans-stats-con-value-fs)] font-[var(--trans-stats-con-value-fw)] text-[var(--trans-stats-con-value-fg)] mt-2",
    },
    {
      conClass:
        "bg-[var(--trans-stats-con-red-bg)] rounded-[12px] h-[5.3rem] w-[25%] p-3 flex justify-between",
      label: "Pending GRNs ",
      value: pendingGRNs?.count || 0,
      desc: `₹${formatIndianNumber(pendingGRNs?.value || 0)} pending`,
      descClass:
        "text-[length:var(--trans-stats-con-desc-fs)] font-[var(--trans-stats-con-desc-fw)] text-[var(--trans-stats-con-red-fg)]",
      icon: <TimerIcon size={16} color="var(--trans-stats-con-red-fg)" />,
      labelClass:
        "text-[length:var(--trans-stats-con-label-fs)] font-[var(--trans-stats-con-label-fw)] text-[var(--trans-stats-con-label-fg)]",
      valueClass:
        "text-[length:var(--trans-stats-con-value-fs)] font-[var(--trans-stats-con-value-fw)] text-[var(--trans-stats-con-value-fg)] mt-2",
    },
    {
      conClass:
        "bg-[var(--trans-stats-con-blue-bg)] rounded-[12px] h-[5.3rem] w-[25%] p-3 flex justify-between",
      label: "Total GRNs",
      value: totalGRNs?.count ?? 0,
      desc: `${totalGRNs?.changePercentage < 0 ? "" : "+"}${
        totalGRNs?.changePercentage || 0
      }% vs yesterday`,
      descClass:
        "text-[length:var(--trans-stats-con-desc-fs)] font-[var(--trans-stats-con-desc-fw)] text-[var(--trans-stats-con-blue-fg)]",
      icon: <FileText size={16} color="var(--trans-stats-con-blue-fg)" />,
      labelClass:
        "text-[length:var(--trans-stats-con-label-fs)] font-[var(--trans-stats-con-label-fw)] text-[var(--trans-stats-con-label-fg)]",
      valueClass:
        "text-[length:var(--trans-stats-con-value-fs)] font-[var(--trans-stats-con-value-fw)] text-[var(--trans-stats-con-value-fg)] mt-2",
    },
  ];

  const summaryArray =
    TransactionSummary?.length > 0 ? (
      <div className="px-[24px] pt-[16px] flex gap-5">
        {TransactionSummary?.map((sum) => (
          <TransSummaryComp sum={sum} />
        ))}
      </div>
    ) : (
      <></>
    );

  return (
    <InnerLayout
      permissionFn={permissionFn}
      module="invoice"
      list={true}
      onclkFn={() => {
        navigate(
          `/add-purchase/${encodeURIComponent(JSON.stringify([]))}/purchase`
        );
      }}
      title="Purchase Order"
    >
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <></>
      ) : (
        <>
          <DynamicTransactionTable
            ViewAction={purchaseViewAction}
            transaction="Purchase Order"
            transaction_type="purchase_order"
            setInvoiceItems={setInvoiceItems}
            invoiceItems={invoiceItems}
            options={options}
            orders={orders}
            setOrders={setOrders}
            tableHeading1={tableHeading1}
            is_checkbox_req={true}
            onClickItemFn={(id) => navigate(`/view-purchase/${id}`)}
            list={true}
            extraFilters={[
              { label: "Item Code", value: "item_code", column: true },
              { label: "Item Name", value: "item_name", column: true }
            ]}
            onclkFn={() => {
              navigate(
                `/add-purchase/${encodeURIComponent(
                  JSON.stringify([])
                )}/purchase?new`
              );
            }}
            cloneFn={(id) => {
              navigate(
                `/add-purchase/${encodeURIComponent(
                  JSON.stringify([])
                )}/purchase?cloneID=${id}`
              );
            }}
            permissionFn={permissionFn}
            module={"purchase_order"}
            path="purchase"
            reload={reload}
            setReload={setReload}
            loading={loading}
            setLoading={setLoading}
            deleteAction={purchaseVoidAction}
            label="Purchase Order"
            showFilter={showFilter}
            filterFn={handleFilterClick}
            setFilterData={setFilterData}
            filterData={filterData}
            paginationProps={paginationProps}
            tableHeading={[
              ...heading,
              ...(!isAdmin
                ? [{ label: "", value: "sync_action", display: false, column: false }]
                : []),
            ]}
            tableBodyData={tableBodyData}
            placeholder="Search (/)"
            filterBy={filterby}
            initialFilter={{
              sort_by: "",
              sort_order: "",
              selectColumn: "pot_invoice_number",
              from: "",
              to: "",
              inputValue: "",
              orderStatus: "",
            }}
            handleSelect={(item) => {
              if (item?.value === "goods_receipt_note") {
                navigate(
                  `/goods-receipt/${encodeURIComponent(
                    JSON.stringify(invoiceItems)
                  )}/order?type=pot&transfer=true`
                );
              } else {
                navigate(
                  `/add-purchase-invoice/${encodeURIComponent(
                    JSON.stringify(invoiceItems)
                  )}/purchase?type=pot&transfer=true`
                );
              }
            }}
            transactionKey="pot"
            actionsPermissions={{
              edit: ["draft","pending"], // Edit allowed for "open" and "draft"
              delete: ["open", "draft"], // Delete allowed only for "open"
              print: ["open", "closed", "draft"], // Print allowed for "open" and "closed"
              send: ["open", "closed", "draft"], // Share allowed for "open" and "closed"
              clone: ["open", "draft", "closed"], // Clone allowed for "open", "draft" and "closed"
            }}
            summaryArray={summaryArray}
          />
        </>
      )}
    </InnerLayout>
  );
};

export default PurchaseTable;
const tableHeading = [
  {
    label: "PURCHASE ORDER NUMBER",
    value: "pot_invoice_number",
    display: true,
    column: true,
  },
  {
    label: "CUSTOMER NAME",
    value: "cmr_name",
    display: true,
    column: true,
  },
  { label: "DATE", value: "update_date", display: true, column: false },
  {
    label: "ORDER STATUS",
    value: "order_status",
    display: false,
    column: false,
  },
  {
    label: <span className="flex justify-end">AMOUNT</span>,
    value: "pot_total_amount",
    display: true,
    column: false,
    className: "justify-end pe-5",
  },
  { label: "", value: "", column: false },
];

const tableHeading1 = [
  { label: "PRODUCT NAME", value: "item_name" },
  { label: "GENERIC NAME", value: "item_generic_name" },
  { label: "ITEM CODE", value: "item_code" },
  { label: "UOM", value: "item_uom" },
  { label: "PRICE (₹)", value: "item_unit_price" },
  { label: "QTY", value: "item_quantity" },
  { label: "OPEN QTY", value: "item_to_be_received" },
  { label: "TOTAL (₹)", value: "item_total_amount" },
];

const options = [
  { value: "goods_receipt_note", label: "Goods Receipt Note" },
  { value: "purchase_invoice", label: "Purchase Invoice" },
];
